import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Gift, 
  Star, 
  TrendingUp,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Reward } from '../types';
import Swal from 'sweetalert2';

interface RewardStatistics {
  total_rewards: number;
  active_rewards: number;
  total_redeemed: number;
  low_stock_rewards: number;
}

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #00d4aa;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #00b894;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    color: ${props => props.color};
  }
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  width: 20px;
  height: 20px;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 720px;
  }
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  border-bottom: 1px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid #f1f3f4;
  font-size: 14px;
  color: #495057;
`;

const RewardImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 12px;
`;

const RewardInfo = styled.div`
  display: flex;
  align-items: center;
`;

const RewardDetails = styled.div``;

const RewardName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const RewardCategory = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const StatusBadge = styled.span<{ isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.isActive ? '#d4edda' : '#f8d7da'};
  color: ${props => props.isActive ? '#155724' : '#721c24'};
`;

const StockBadge = styled.span<{ isLow: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.isLow ? '#fff3cd' : '#d1ecf1'};
  color: ${props => props.isLow ? '#856404' : '#0c5460'};
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' | 'view' }>`
  padding: 8px;
  border: none;
  border-radius: 6px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => {
    switch (props.variant) {
      case 'edit': return '#17a2b8';
      case 'delete': return '#dc3545';
      case 'view': return '#6c757d';
      default: return '#00d4aa';
    }
  }};
  color: white;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: #f8f9fa;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: 1px solid ${props => props.variant === 'primary' ? '#00d4aa' : '#dee2e6'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.variant === 'primary' ? '#00d4aa' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#6c757d'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#00b894' : '#f8f9fa'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6c757d;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #495057;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin-bottom: 24px;
`;

export default function RewardsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<RewardStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_required: '',
    stock_quantity: '',
    category: '',
    is_active: true,
    expires_at: '',
    store_id: ''
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const getToken = () => localStorage.getItem('auth_token');

  const loadStores = useCallback(async () => {
    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/stores`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStores(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    }
  }, []);

  const loadRewards = useCallback(async () => {
    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/rewards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRewards(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar prêmios:', error);
    }
  }, []);

  const loadStatistics = useCallback(async () => {
    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/rewards-statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadRewards();
      loadStatistics();
      loadStores();
    }
  }, [isAuthenticated, loadRewards, loadStatistics, loadStores]);

  const handleAddReward = () => {
    setModalType('add');
    setFormData({
      name: '',
      description: '',
      points_required: '',
      stock_quantity: '',
      category: '',
      is_active: true,
      expires_at: '',
      store_id: ''
    });
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedReward(null);
    setShowModal(true);
  };

  const handleEditReward = (reward: Reward) => {
    setModalType('edit');

    // Formatar data de expiração para o formato yyyy-MM-dd se existir
    let expiresAtFormatted = '';
    if (reward.expires_at) {
      const date = new Date(reward.expires_at);
      expiresAtFormatted = date.toISOString().split('T')[0];
    }

    setFormData({
      name: reward.name,
      description: reward.description,
      points_required: reward.points_required.toString(),
      stock_quantity: reward.stock_quantity.toString(),
      category: reward.category || '',
      is_active: reward.is_active,
      expires_at: expiresAtFormatted,
      store_id: reward.store_id.toString()
    });
    setSelectedImage(null);
    setImagePreview(reward.image_url || null);
    setSelectedReward(reward);
    setShowModal(true);
  };

  const handleViewReward = (reward: Reward) => {
    setModalType('view');
    setSelectedReward(reward);
    setShowModal(true);
  };

  const handleDeleteReward = async (rewardId: number) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja deletar este prêmio?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/rewards/${rewardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Prêmio deletado com sucesso!');
        loadRewards();
        loadStatistics();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao deletar prêmio');
      }
    } catch (error) {
      showError('Erro ao deletar prêmio');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const url = modalType === 'add'
        ? `${apiUrl}/rewards`
        : `${apiUrl}/rewards/${selectedReward?.id}`;

      // Criar FormData para upload de arquivo
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('points_required', formData.points_required);
      formDataToSend.append('stock_quantity', formData.stock_quantity);
      formDataToSend.append('category', formData.category || 'product');
      formDataToSend.append('is_active', formData.is_active ? '1' : '0');

      if (formData.expires_at) {
        formDataToSend.append('expires_at', formData.expires_at);
      }

      if (formData.store_id) {
        formDataToSend.append('store_id', formData.store_id);
      }

      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      // Laravel doesn't handle file uploads with PUT, so use POST with _method override
      if (modalType === 'edit') {
        formDataToSend.append('_method', 'PUT');
      }

      const response = await fetch(url, {
        method: 'POST', // Always use POST for FormData
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formDataToSend
      });

      if (response.ok) {
        showSuccess(`Prêmio ${modalType === 'add' ? 'criado' : 'atualizado'} com sucesso!`);
        setShowModal(false);
        loadRewards();
        loadStatistics();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao salvar prêmio');
      }
    } catch (error) {
      showError('Erro ao salvar prêmio');
    }
  };

  const filteredRewards = rewards.filter(reward =>
    reward.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reward.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Carregando...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <Title>Gerenciar Prêmios</Title>
        <AddButton onClick={handleAddReward}>
          <Plus size={16} />
          Adicionar Prêmio
        </AddButton>
      </Header>

      <StatisticsGrid>
        <StatCard>
          <StatIcon color="#00d4aa">
            <Gift size={24} />
          </StatIcon>
          <StatValue>{statistics?.total_rewards || 0}</StatValue>
          <StatLabel>Total de Prêmios</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#28a745">
            <Star size={24} />
          </StatIcon>
          <StatValue>{statistics?.active_rewards || 0}</StatValue>
          <StatLabel>Prêmios Ativos</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#17a2b8">
            <TrendingUp size={24} />
          </StatIcon>
          <StatValue>{statistics?.total_redeemed || 0}</StatValue>
          <StatLabel>Total Resgatado</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon color="#ffc107">
            <AlertTriangle size={24} />
          </StatIcon>
          <StatValue>{statistics?.low_stock_rewards || 0}</StatValue>
          <StatLabel>Estoque Baixo</StatLabel>
        </StatCard>
      </StatisticsGrid>

      <SearchContainer>
        <SearchIcon />
        <SearchInput
          type="text"
          placeholder="Buscar prêmios por nome ou categoria..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <TableContainer>
        {filteredRewards.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🎁</EmptyIcon>
            <EmptyTitle>Nenhum prêmio encontrado</EmptyTitle>
            <EmptyDescription>
              {rewards.length === 0 
                ? 'Comece criando seu primeiro prêmio para o sistema de fidelidade.'
                : 'Nenhum prêmio corresponde aos critérios de busca.'
              }
            </EmptyDescription>
            {rewards.length === 0 && (
              <AddButton onClick={handleAddReward}>
                <Plus size={16} />
                Criar Primeiro Prêmio
              </AddButton>
            )}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>Prêmio</TableHeaderCell>
                <TableHeaderCell>Pontos</TableHeaderCell>
                <TableHeaderCell>Estoque</TableHeaderCell>
                <TableHeaderCell>Resgatado</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Ações</TableHeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {filteredRewards.map((reward) => (
                <TableRow key={reward.id}>
                  <TableCell>
                    <RewardInfo>
                      {reward.image_url ? (
                        <RewardImage src={reward.image_url} alt={reward.name} />
                      ) : (
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '8px', 
                          background: '#f8f9fa', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          <Gift size={20} color="#6c757d" />
                        </div>
                      )}
                      <RewardDetails>
                        <RewardName>{reward.name}</RewardName>
                        {reward.category_name && (
                          <RewardCategory>{reward.category_name}</RewardCategory>
                        )}
                      </RewardDetails>
                    </RewardInfo>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: '600', color: '#00d4aa' }}>
                      {reward.points_required} pts
                    </div>
                  </TableCell>
                  <TableCell>
                    <StockBadge isLow={reward.stock_quantity <= 5}>
                      {reward.stock_quantity} un.
                    </StockBadge>
                  </TableCell>
                  <TableCell>{reward.redeemed_count}</TableCell>
                  <TableCell>
                    <StatusBadge isActive={reward.is_active}>
                      {reward.is_active ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <ActionButton variant="view" onClick={() => handleViewReward(reward)}>
                      <Eye size={16} />
                    </ActionButton>
                    <ActionButton variant="edit" onClick={() => handleEditReward(reward)}>
                      <Edit size={16} />
                    </ActionButton>
                    <ActionButton variant="delete" onClick={() => handleDeleteReward(reward.id)}>
                      <Trash2 size={16} />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <Modal isOpen={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {modalType === 'add' && 'Adicionar Prêmio'}
              {modalType === 'edit' && 'Editar Prêmio'}
              {modalType === 'view' && 'Detalhes do Prêmio'}
            </ModalTitle>
            <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
          </ModalHeader>

          {modalType === 'view' && selectedReward ? (
            <div>
              <FormGroup>
                <Label>Nome</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  {selectedReward.name}
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Descrição</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', minHeight: '60px' }}>
                  {selectedReward.description}
                </div>
              </FormGroup>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormGroup>
                  <Label>Pontos Necessários</Label>
                  <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {selectedReward.points_required} pontos
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Estoque</Label>
                  <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {selectedReward.stock_quantity} unidades
                  </div>
                </FormGroup>
              </div>
              {selectedReward.category && (
                <FormGroup>
                  <Label>Categoria</Label>
                  <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {selectedReward.category}
                  </div>
                </FormGroup>
              )}
              <FormGroup>
                <Label>Status</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <StatusBadge isActive={selectedReward.is_active}>
                    {selectedReward.is_active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                </div>
              </FormGroup>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Nome do Prêmio *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Ex: Desconto de 20%"
                />
              </FormGroup>

              <FormGroup>
                <Label>Descrição *</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  placeholder="Descreva os detalhes do prêmio..."
                />
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormGroup>
                  <Label>Pontos Necessários *</Label>
                  <Input
                    type="number"
                    value={formData.points_required}
                    onChange={(e) => setFormData({...formData, points_required: e.target.value})}
                    required
                    min="1"
                    placeholder="100"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Quantidade em Estoque *</Label>
                  <Input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                    required
                    min="0"
                    placeholder="50"
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label>Categoria</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="discount">Desconto</option>
                  <option value="product">Produto</option>
                  <option value="service">Serviço</option>
                  <option value="experience">Experiência</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Imagem do Prêmio</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '2px solid #dee2e6'
                      }}
                    />
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Data de Expiração</Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                />
              </FormGroup>

              {user && user.role && ['admin', 'super_admin'].includes(user.role) && (
                <FormGroup>
                  <Label>Loja *</Label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => setFormData({...formData, store_id: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Selecione uma loja</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              )}

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  Prêmio ativo
                </CheckboxLabel>
              </FormGroup>

              <ButtonGroup>
                <Button type="button" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  {modalType === 'add' ? 'Criar Prêmio' : 'Salvar Alterações'}
                </Button>
              </ButtonGroup>
            </form>
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
}
